import { zodResolver } from '@hookform/resolvers/zod'
import {
  CaretLeftIcon,
  CaretRightIcon,
  ClockIcon,
  HandCoinsIcon,
  MinusIcon,
  PlusIcon,
} from '@phosphor-icons/react'
import { useMemo } from 'react'
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import z from 'zod'

import { useExtendVisitorParkingSession } from '@/api/visitor-parking/extend-visitor-parking-session'
import { useGetExtendVisitorParkingCalculation } from '@/api/visitor-parking/get-extend-visitor-parking-calculation'
import { useGetVisitorParkingSession } from '@/api/visitor-parking/get-visitor-parking-session'
import { Button } from '@/components/business/visitor-parking/button'
import { ButtonIcon } from '@/components/business/visitor-parking/button-icon'
import { Card } from '@/components/business/visitor-parking/card'
import { DetailsCard } from '@/components/business/visitor-parking/details-card'
import { ErrorMessage } from '@/components/business/visitor-parking/error-message'
import { HelpSection } from '@/components/business/visitor-parking/help-section'
import { Icon } from '@/components/ui/icon'
import { useSearchParams } from '@/hooks'
import {
  formatCurrency,
  formatDuration,
  formatRelativeParkingEndTime,
  getRelativePaymentSuccessReturnUrl,
} from '@/pages/visitor-parking/visitor-parking.utils'
import { VisitorParkingFormLayout } from '@/pages/visitor-parking/visitor-parking-form-layout'
import { getApiErrorMessage } from '@/utils/error'

import {
  VISITOR_PARKING_TICKET_NUMBER_URL_KEY,
  VISITOR_PARKING_TOKEN_URL_KEY,
} from '../visitor-parking.constants'
import { useVisitorParkingStore } from '../visitor-parking.store'
import * as S from './extend-parking-duration-form.styled'

interface ExtendParkingDurationFormProps {
  onBack: () => void
  onNext: () => void
}

export const ExtendParkingDurationForm = ({ onBack, onNext }: ExtendParkingDurationFormProps) => {
  const { t } = useTranslation(['visitor-parking'])
  const navigate = useNavigate()
  const { parsedQuery } = useSearchParams({
    initialState: {
      [VISITOR_PARKING_TICKET_NUMBER_URL_KEY]: undefined,
      [VISITOR_PARKING_TOKEN_URL_KEY]: undefined,
    },
    schema: z.object({
      [VISITOR_PARKING_TICKET_NUMBER_URL_KEY]: z.string().optional(),
      [VISITOR_PARKING_TOKEN_URL_KEY]: z.string().optional(),
    }),
  })
  const ticketNumber = parsedQuery[VISITOR_PARKING_TICKET_NUMBER_URL_KEY] ?? ''
  const token = parsedQuery[VISITOR_PARKING_TOKEN_URL_KEY] ?? ''
  const { data: parkingSession } = useGetVisitorParkingSession({ ticketNumber })
  const { mutateAsync: extendVisitorParkingSession, error: extendVisitorParkingSessionError } =
    useExtendVisitorParkingSession(ticketNumber)
  const { setPaymentSession } = useVisitorParkingStore()

  const schema = useMemo(
    () =>
      z.object({
        duration: z
          .number()
          .min(1, t('visitor-parking:extendParkingDurationForm.validation.required')),
      }),
    [t],
  )

  const form = useForm<{ duration: number }>({
    defaultValues: {
      duration: 1,
    },
    mode: 'onChange',
    resolver: zodResolver(schema),
  })

  const durationValue = useWatch({
    control: form.control,
    name: 'duration',
  })

  const {
    data: calculationData,
    isLoading,
    error: calculationError,
  } = useGetExtendVisitorParkingCalculation({
    duration: durationValue,
    sessionId: parkingSession?.sessionId ?? 0,
  })

  const onSubmit = async (values: { duration: number }) => {
    const session = await extendVisitorParkingSession({
      duration: values.duration,
      ticketNumber: parkingSession?.ticketNumber ?? ticketNumber,
    })

    if (session.amount === 0) {
      navigate(getRelativePaymentSuccessReturnUrl(session.ticketNumber, token, true))
      return
    }

    setPaymentSession({
      amount: session.amount,
      clientSecret: session.clientSecret,
      sessionId: session.sessionId,
      ticketNumber: session.ticketNumber,
      visitorId: session.visitorId,
    })
    onNext()
  }

  return (
    <VisitorParkingFormLayout
      footer={
        <>
          <ButtonIcon type="button" onClick={onBack}>
            <Icon icon={CaretLeftIcon} />
          </ButtonIcon>
          <Button
            type="submit"
            disabled={!form.formState.isValid || isLoading || !!calculationError}
            loading={form.formState.isSubmitting}
            onClick={form.handleSubmit(onSubmit)}
          >
            {t('visitor-parking:extendParkingDurationForm.next')} <Icon icon={CaretRightIcon} />
          </Button>
        </>
      }
    >
      <FormProvider {...form}>
        <S.Title>{t('visitor-parking:extendParkingDurationForm.title')}</S.Title>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Card>
            <S.Heading>{t('visitor-parking:extendParkingDurationForm.extendPrompt')}</S.Heading>
            <S.DurationLabel htmlFor="duration">
              {t('visitor-parking:extendParkingDurationForm.durationLabel')}
            </S.DurationLabel>
            <Controller
              control={form.control}
              name="duration"
              render={({ field: { onChange, value } }) => (
                <S.DurationSelector>
                  <S.DurationPreview>{formatDuration(value, t)}</S.DurationPreview>
                  <S.DurationSelectorButton
                    disabled={value <= 1}
                    type="button"
                    onClick={() => onChange(value - 1)}
                  >
                    <Icon icon={MinusIcon} />
                  </S.DurationSelectorButton>
                  <S.DurationSelectorButton type="button" onClick={() => onChange(value + 1)}>
                    <Icon icon={PlusIcon} />
                  </S.DurationSelectorButton>
                </S.DurationSelector>
              )}
            />
            <DetailsCard
              icon={ClockIcon}
              loading={isLoading}
              title={t('visitor-parking:extendParkingDurationForm.endTime')}
              value={formatRelativeParkingEndTime(calculationData?.endDate, t)}
            />
            <DetailsCard
              icon={HandCoinsIcon}
              loading={isLoading}
              title={t('visitor-parking:extendParkingDurationForm.fee')}
              value={formatCurrency(calculationData?.amount)}
            />
            <S.CostDescription>
              {t('visitor-parking:extendParkingDurationForm.costDescription')}
            </S.CostDescription>

            {extendVisitorParkingSessionError || calculationError ? (
              <ErrorMessage
                message={getApiErrorMessage(calculationError || extendVisitorParkingSessionError)}
              />
            ) : null}
          </Card>
        </form>
        <HelpSection />
      </FormProvider>
    </VisitorParkingFormLayout>
  )
}
